import React, { useState, useEffect, useMemo } from 'react';
import { FileText, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { pagesApi } from '../../../utils/api';

/**
 * Strip Cloudflare-injected scripts that can't resolve inside srcDoc iframes
 */
const stripCloudflareScripts = (html) => {
  if (!html) return html;
  return html.replace(/<script[^>]*src="\/cdn-cgi\/[^"]*"[^>]*><\/script>/gi, '');
};

/**
 * Inject a base tag to make links open in parent window, not iframe
 * This prevents the app from rendering inside itself when internal links are clicked
 */
const injectBaseTarget = (html) => {
  if (!html) return html;
  
  // If there's already a <head>, inject base tag at the start of it
  if (html.includes('<head>')) {
    return html.replace('<head>', '<head><base target="_top">');
  }
  // If there's no head but has html tag, add head with base
  if (html.includes('<html')) {
    return html.replace(/<html[^>]*>/, '$&<head><base target="_top"></head>');
  }
  // Otherwise, prepend base tag
  return `<base target="_top">${html}`;
};

/**
 * Renders content for PAGE type spaces
 * Config can include:
 * - page_id: ID of a page to fetch from the database
 * - content: HTML string to render directly
 * - url: External URL to embed in iframe
 */
const PageSpace = ({ space, theme = 'dark' }) => {
  const { config, name } = space;
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch page content when page_id is provided
  useEffect(() => {
    if (!config?.page_id) {
      setPageContent(null);
      return;
    }

    const fetchPage = async () => {
      try {
        setLoading(true);
        setError(null);
        const page = await pagesApi.get(config.page_id);
        setPageContent(page);
      } catch (err) {
        console.error('Failed to fetch page:', err);
        setError('Failed to load page content');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [config?.page_id]);

  // Loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <Loader2 size={48} className="mb-4 animate-spin" />
        <p className="text-sm">Loading page...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-red-400">
        <AlertCircle size={48} className="mb-4" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  // No config - show placeholder
  if (!config) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <FileText size={48} className="mb-4 opacity-50" />
        <h2 className="text-xl font-semibold mb-2">{name}</h2>
        <p className="text-sm">This page has no content configured yet.</p>
        <p className="text-xs mt-2 text-gray-500">
          Configure content in the Space Dashboard.
        </p>
      </div>
    );
  }

  // Render page from database
  if (config.page_id && pageContent) {
    return (
      <div className="h-full overflow-auto">
        <iframe
          srcDoc={injectBaseTarget(stripCloudflareScripts(pageContent.content)) || '<p>No content</p>'}
          className="w-full h-full border-0"
          title={pageContent.name || name}
          allow="clipboard-read; clipboard-write"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-pointer-lock allow-presentation"
        />
      </div>
    );
  }

  // Render iframe for external URL
  if (config.url) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 bg-gray-800/50 border-b border-gray-700/50">
          <span className="text-sm text-gray-400 truncate">{config.url}</span>
          <a 
            href={config.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            <ExternalLink size={14} />
            <span className="text-xs">Open in new tab</span>
          </a>
        </div>
        {/* Note: allow-same-origin removed for security - external URLs are isolated from parent page */}
        <iframe
          src={config.url}
          className="flex-1 w-full border-0"
          title={name}
          allow="clipboard-read; clipboard-write"
          sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-pointer-lock"
        />
      </div>
    );
  }

  // Render direct HTML content
  if (config.content) {
    return (
      <div className="h-full overflow-auto">
        {/* Note: allow-same-origin removed for security - prevents XSS attacks via user-generated content */}
        <iframe
          srcDoc={injectBaseTarget(stripCloudflareScripts(config.content))}
          className="w-full h-full border-0"
          title={name}
          allow="clipboard-read; clipboard-write"
          sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-pointer-lock"
        />
      </div>
    );
  }

  // No page selected for PAGE type
  if (!config.page_id) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <FileText size={48} className="mb-4 opacity-50" />
        <h2 className="text-xl font-semibold mb-2">{name}</h2>
        <p className="text-sm">No page selected.</p>
        <p className="text-xs mt-2 text-gray-500">
          Select a page in the Space Dashboard.
        </p>
      </div>
    );
  }

  // Fallback
  return (
    <div className="h-full flex items-center justify-center text-gray-400">
      <p>Unknown page configuration</p>
    </div>
  );
};

export default PageSpace;
